import { modelTitleState } from '@/components/canvas/utils/modelTitle'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { type NodeProps, useNodeConnections, useNodesData, useStore } from '@xyflow/react'
import { useParams } from '@tanstack/react-router'
import { Node3D } from '@/components/canvas/nodes'
import type { GenieNodeState, AiModelPurpose, NodeOrchestratorState } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import type { HandleMeta } from '@/lib/canvas/types'
import { PURPOSE_HANDLES } from '@/lib/canvas/configs/handles'
import { useNodeModels, getDefaultModel, findModelById } from '@/hooks/useNodeModels'
import { useWalletBalance } from '@/hooks/useWalletBalance'
import { usePinnedParameters } from '@/components/canvas/nodes/pinned-parameters/usePinnedParameters'

/**
 * ThreeDNodeNode - React Flow custom node
 *
 * Renders the 3D model node through BaseNode, the same prompt/upload/run layout
 * as the image and video nodes.
 */

export interface ThreeDNodeNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

/** Default handles from centralized config */
const DEFAULT_3D_HANDLES: HandleMeta[] = PURPOSE_HANDLES.ThreeDNode.default

/** The job queue has no 3D job type yet, so a run cannot be submitted */
const GENERATION_UNAVAILABLE = '3D generation is not connected to the job queue yet'

const IMAGE_TO_3D_PATTERN = /image[-_ ]?to[-_ ]?3d/i

function resolveSubPurpose(saved: unknown, model?: { subPurpose?: string; endpointId?: string; name: string }): string | undefined {
  if (typeof saved === 'string' && saved) return saved
  if (model?.subPurpose) return model.subPurpose
  if (model && IMAGE_TO_3D_PATTERN.test(`${model.endpointId ?? ''} ${model.name}`)) return 'image-to-3d'
  return undefined
}

function ThreeDNodeNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as ThreeDNodeNodeData
  const { state: genieState, onHoverChange } = nodeData
  const purpose = (genieState.purpose as string) || undefined
  const onStateChange = useNodeStateChange(id)
  const { id: canvasIdParam } = useParams({ strict: false })
  const canvasId = canvasIdParam as string | undefined

  const { data: threeDModels } = useNodeModels('3d', undefined, (purpose as AiModelPurpose) || 'create')
  const { balance: walletBalance } = useWalletBalance()

  const [orchestratorState, setOrchestratorState] = useState<NodeOrchestratorState>({ status: 'idle' })

  // Reactively resolve connected image URL from upstream node
  const connectedImage = useStore(useCallback((s) => {
    const incomingEdges = s.edges.filter((e) => e.target === id && e.targetHandle === 'image-input')
    for (const edge of incomingEdges) {
      const sourceNode = s.nodes.find((n) => n.id === edge.source)
      const sourceState = (sourceNode?.data as Record<string, unknown> | undefined)?.state as Record<string, unknown> | undefined
      if (!sourceNode || !sourceState) continue

      if (sourceNode.type === 'ImageFile') {
        const fileMeta = sourceState.fileMetadata as Record<string, unknown> | undefined
        if (fileMeta?.publicUrl) return fileMeta.publicUrl as string
      }
      if (sourceNode.type === 'ImageNode') {
        const createdItems = sourceState.createdItems as Array<{ type: string; content: string }> | undefined
        const lastImage = createdItems?.filter(i => i.type === 'image').pop()
        if (lastImage?.content) return lastImage.content
      }
      const images = sourceState.images as string[] | undefined
      if (images && images.length > 0) return images[images.length - 1]
    }
    return null
  }, [id]))

  // Reactively read connected prompt from upstream text node
  const incomingConnections = useNodeConnections({ handleType: 'target' })
  const sourceNodeIds = useMemo(() => incomingConnections.map(c => c.source), [incomingConnections])
  const sourceNodesData = useNodesData(sourceNodeIds)

  const connectedPrompt = useMemo(() => {
    for (const nodeEntry of sourceNodesData) {
      const sourceState = (nodeEntry?.data as Record<string, unknown> | undefined)?.state as Record<string, unknown> | undefined
      if (!sourceState) continue
      if (nodeEntry.type === 'TextFile' || nodeEntry.type === 'TextNode') {
        const createdItems = sourceState.createdItems as Array<{ type: string; content: string }> | undefined
        const latestText = createdItems?.[createdItems.length - 1]
        if (latestText?.type === 'text' && typeof latestText.content === 'string' && latestText.content.trim()) {
          return latestText.content
        }
        const prompt = sourceState.prompt
        if (typeof prompt === 'string' && prompt.trim()) return prompt
      }
    }
    return null
  }, [sourceNodesData])

  const handleChange = useCallback((dataOrUpdater: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => {
    if (typeof dataOrUpdater === 'function') {
      onStateChange((prev) => {
        const result = dataOrUpdater(prev)
        if (result.orchestrator !== undefined) {
          setOrchestratorState(result.orchestrator as NodeOrchestratorState)
          const { orchestrator: _orchestrator, ...rest } = result
          return rest
        }
        return result
      })
      return
    }

    const d = dataOrUpdater
    if (d.prompt !== undefined) onStateChange(prev => ({ ...prev, prompt: d.prompt }))
    if (d.title !== undefined) {
      onStateChange(prev => ({ ...prev, title: d.title, titleSource: 'custom', data: { ...(prev.data as Record<string, unknown> || {}), title: d.title } }))
    }
    if (d.images !== undefined) onStateChange(prev => ({ ...prev, images: d.images }))
    if (d.createdItems !== undefined) onStateChange(prev => ({ ...prev, createdItems: d.createdItems }))
    if (d.collapsed !== undefined) onStateChange(prev => ({ ...prev, collapsed: d.collapsed }))
    if (d.orchestrator !== undefined) setOrchestratorState(d.orchestrator as NodeOrchestratorState)
  }, [onStateChange])

  const handleSelect = useCallback(() => {}, [])

  // Resolve active model for cost estimation and input requirements
  const defaultModel = useMemo(() => getDefaultModel(threeDModels), [threeDModels])
  const activeModel = useMemo(() => {
    const savedModelId = typeof genieState.modelId === 'string' ? genieState.modelId : undefined
    return (savedModelId && findModelById(threeDModels, savedModelId)) || defaultModel
  }, [threeDModels, genieState.modelId, defaultModel])

  const subPurpose = resolveSubPurpose(genieState.subPurpose, activeModel ?? undefined)

  const handleGenerate = useCallback(() => {
    setOrchestratorState({ status: 'error', message: GENERATION_UNAVAILABLE })
  }, [])

  // Programmatic action handler
  const action = genieState._action
  const actionTimestamp = action?.ts
  useEffect(() => {
    if (!action) return
    switch (action.type) {
      case 'generate': handleGenerate(); break
      case 'cancel': setOrchestratorState({ status: 'idle' }); break
      case 'clear': onStateChange(prev => ({ ...prev, createdItems: [] })); break
      case 'update':
        if (action.params?.prompt !== undefined) onStateChange(prev => ({ ...prev, prompt: action.params.prompt }))
        break
    }
    onStateChange(prev => ({ ...prev, _action: null }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionTimestamp, action, onStateChange])

  // Pinned parameters support
  const pinnedNames = useMemo(() => {
    const raw = genieState.pinnedParameters
    return Array.isArray(raw) ? (raw as string[]) : []
  }, [genieState.pinnedParameters])

  const { parameters: pinnedParams, values: pinnedValues } = usePinnedParameters({
    pinnedNames,
    nodeType: '3d',
    modelFamilySlug: (genieState.modelFamilySlug as string | undefined) || activeModel?.modelFamilySlug,
    modelId: (genieState.modelId as string | undefined) || activeModel?.id,
    purpose: (purpose as AiModelPurpose) || 'create',
    settings: genieState.settings as Record<string, unknown> | undefined,
  })

  const handlePinnedParameterChange = useCallback((paramName: string, value: unknown) => {
    onStateChange(prev => ({
      ...prev,
      settings: { ...((prev.settings as Record<string, unknown>) || {}), [paramName]: value },
    }))
  }, [onStateChange])

  const pinnedModelsList = useMemo(() => {
    if (!pinnedNames.includes('__model__')) return undefined
    return threeDModels.map(m => ({ id: m.id, name: m.name }))
  }, [threeDModels, pinnedNames])

  const handlePinnedModelChange = useCallback((modelId: string) => {
    const model = findModelById(threeDModels, modelId)
    if (!model) return
    onStateChange(prev => ({
      ...prev,
      ...modelTitleState(prev, model.name),
      modelId: model.id, modelFamilySlug: model.modelFamilySlug,
    }))
  }, [threeDModels, onStateChange])

  const nodeProps = {
    id,
    selected: selected || genieState.selected,
    initialData: { ...genieState, ...(activeModel?.name ? modelTitleState(genieState, activeModel.name) : {}) },
    connectedPrompt,
    connectedImage,
    orchestratorState,
    onChange: handleChange,
    onStateChange,
    onSelect: handleSelect,
    onGenerate: handleGenerate,
    onHoverChange,
    loading: false,
    error: null,
    canvasId: (genieState.canvasId as string) || canvasId,
    costProvider: activeModel?.provider ?? null,
    costModel: activeModel?.id ?? null,
    walletBalance: walletBalance ?? undefined,
    pinnedParameters: pinnedParams.length > 0 ? pinnedParams : undefined,
    pinnedParameterValues: pinnedParams.length > 0 ? pinnedValues : undefined,
    onPinnedParameterChange: (pinnedParams.length > 0 || pinnedNames.includes('__model__')) ? handlePinnedParameterChange : undefined,
    pinnedModelName: pinnedNames.includes('__model__') ? (activeModel?.name || activeModel?.id || 'No model') : undefined,
    pinnedModels: pinnedModelsList,
    onPinnedModelChange: pinnedNames.includes('__model__') ? handlePinnedModelChange : undefined,
  }

  const handles: HandleMeta[] = nodeData.handles || DEFAULT_3D_HANDLES

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <Node3D {...nodeProps} subPurpose={subPurpose} />
    </>
  )
}

export const ThreeDNodeNode = React.memo(
  ThreeDNodeNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as ThreeDNodeNodeData
    const nextData = nextProps.data as unknown as ThreeDNodeNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.titleSource === nextData.state.titleSource &&
      prevData.state.modelName === nextData.state.modelName &&
      prevData.state.prompt === nextData.state.prompt &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.state.locked === nextData.state.locked &&
      prevData.state.images === nextData.state.images &&
      prevData.state.createdItems === nextData.state.createdItems &&
      prevData.state._action === nextData.state._action &&
      prevData.state.pinnedParameters === nextData.state.pinnedParameters &&
      prevData.state.settings === nextData.state.settings &&
      prevData.state.purpose === nextData.state.purpose &&
      prevData.state.subPurpose === nextData.state.subPurpose &&
      prevData.state.modelId === nextData.state.modelId &&
      prevData.state.modelFamilySlug === nextData.state.modelFamilySlug &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)
ThreeDNodeNode.displayName = 'ThreeDNodeNode'
